import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMenuManagement } from "../../../context/MenuManagementContext";
import { isItemActive } from "./navMenuUtils";
import MoreMenuSheet from "./MoreMenuSheet";
import "./bottomnav.css";

// Titles pulled to the front of the bottom bar as direct-tap icons.
// Everything else (and all submenus) lives behind "More". Easy to retune later.
const PINNED_MODULES = ["Dashboard", "Academy Management", "CM Trophy", "Protsaahan"];

// Inline fallbacks so the popover's text/rows can never render blank from a
// stray stylesheet mismatch — same defensive approach used in MoreMenuSheet.js.
const popoverRowStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "0.6rem 0.9rem",
  borderRadius: 10,
  fontSize: "0.85rem",
  color: "#33334d",
  background: "transparent",
  border: "none",
  textDecoration: "none",
  cursor: "pointer",
  boxSizing: "border-box",
};
const popoverRowActiveStyle = { background: "#8757e8", color: "#ffffff", fontWeight: 600 };

const BottomNav = () => {
  const { sideabarmenuDataList, fetchSidebarMenuList } = useMenuManagement();
  const [moreOpen, setMoreOpen] = useState(false);
  const [openPopoverFor, setOpenPopoverFor] = useState("");
  const location = useLocation();
  const firstPath = useRef(location.pathname);

  useEffect(() => {
    const userType = localStorage.getItem("user_type");
    if (!userType || userType === "admin") {
      fetchSidebarMenuList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close any open popover on navigation.
  useEffect(() => {
    if (location.pathname !== firstPath.current) {
      setOpenPopoverFor("");
    }
    firstPath.current = location.pathname;
  }, [location.pathname]);

  const menuItems = sideabarmenuDataList?.data || [];
  const pinnedItems = PINNED_MODULES
    .map((title) => menuItems.find((m) => m.title?.toLowerCase() === title.toLowerCase()))
    .filter(Boolean);

  return (
    <>
      {openPopoverFor && (
        <div className="bn-popover-catcher" onClick={() => setOpenPopoverFor("")} />
      )}

      <nav className="bn-bar">
        {pinnedItems.map((item) => {
          // A submenu with a single row is just a worse link than the parent itself
          // (e.g. Dashboard -> [Dashboard]) — collapse it to a direct tap.
          const hasChildren = item.children && item.children.length > 1;
          const active = isItemActive(item, location.pathname);
          const popoverOpen = openPopoverFor === item.title;

          if (!hasChildren) {
            const directUrl = item.url || item.to || item.children?.[0]?.url || item.children?.[0]?.to;
            return (
              <Link key={item.title} to={directUrl || "#"} className="bn-item">
                <span className={`bn-icon-badge ${active ? "bn-icon-badge--active" : ""}`}>
                  {item.icon && <i className={item.icon} />}
                </span>
                <span className={`bn-label ${active ? "bn-label--active" : ""}`}>{item.title}</span>
              </Link>
            );
          }

          return (
            <div key={item.title} className="bn-item-wrap">
              <button
                type="button"
                className="bn-item bn-item--button"
                onClick={() => setOpenPopoverFor(popoverOpen ? "" : item.title)}
              >
                <span className={`bn-icon-badge ${active || popoverOpen ? "bn-icon-badge--active" : ""}`}>
                  {item.icon && <i className={item.icon} />}
                  <span className="bn-icon-dot" />
                </span>
                <span className={`bn-label ${active || popoverOpen ? "bn-label--active" : ""}`}>{item.title}</span>
              </button>

              {popoverOpen && (
                <div className="bn-popover" onClick={(e) => e.stopPropagation()}>
                  <div className="bn-popover-title">{item.title}</div>
                  {item.children.map((child) => {
                    const childUrl = child.url || child.to || child.children?.[0]?.url;
                    const childActive = isItemActive(child, location.pathname);
                    return (
                      <Link
                        key={child.title}
                        to={childUrl || "#"}
                        className="bn-popover-item"
                        style={childActive ? { ...popoverRowStyle, ...popoverRowActiveStyle } : popoverRowStyle}
                        onClick={() => setOpenPopoverFor("")}
                      >
                        {child.title}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <button
          type="button"
          className="bn-item bn-item--button"
          onClick={() => setMoreOpen(true)}
          aria-label="More navigation options"
        >
          <span className={`bn-icon-badge ${moreOpen ? "bn-icon-badge--active" : ""}`}>
            <i className="bi bi-grid-3x3-gap-fill" />
          </span>
          <span className={`bn-label ${moreOpen ? "bn-label--active" : ""}`}>More</span>
        </button>
      </nav>

      <MoreMenuSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
};

export default BottomNav;
